/* PR 53 fixes · F4 (S19) — no silent aging fit on the AP / AR aging worksheet.
   A retention payable stays trade by default. When the aging total (or the aging's Account column) points the other way, the
   account shows a SUGGESTION ("Aging appears to exclude 3120 - 11,125,158.85") and "Needs classification" until the auditor
   ticks it (override: untick Trade, or "Keep as trade") AND gives a reason. The figures never move by the suggestion. */
import React from "react";
import { useI18n } from "@/contexts/I18nContext";
import { fmtNumber } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter

const NF = { format: (n) => fmtNumber(n) };   // STEP 3 · rule 2 · B-2e
const fill = (s, o) => String(s || "").replace(/\{(\w+)\}/g, (_, k) => (o[k] ?? ""));

/** Does this account still need classification on screen (the draft overrides / reasons included)? */
export function agingNeedsClass(a, overrides, reasons) {
  const has = !!a && a.coa_id in (overrides || {});
  if (a?.aging_suggestion) return !(has && String((reasons || {})[a.coa_id] || "").trim());
  return !!a?.needs_classification && !has;
}

export function AgingSuggestion({ a, overrides, reasons, disabled, onReason, onKeepTrade }) {
  const { t } = useI18n();
  const s = a?.aging_suggestion;
  if (!s) return null;
  const has = a.coa_id in (overrides || {});
  const reason = String((reasons || {})[a.coa_id] || "");
  const needs = agingNeedsClass(a, overrides, reasons);
  const text = fill(t(s.basis === "listing" ? "p53s.notListed" : "p53s.appearsToExclude"),
    { code: a.code || a.name || "", amount: NF.format(Number(s.amount) || 0) });
  return (
    <div className="space-y-1" data-testid={`hfws-acct-suggest-${a.coa_id}`}>
      {needs ? (
        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
              data-testid={`hfws-acct-needs-${a.coa_id}`}>{t("hfWs.aging.needsClassification")}</span>
      ) : (
        <span className="text-[10px] text-emerald-700">{t("hfWs.aging.overridden")}</span>
      )}
      <div className="text-[10px] text-amber-800 dark:text-amber-300" data-testid={`hfws-acct-suggest-text-${a.coa_id}`}>{text}</div>
      {!has && <div className="text-[10px] text-muted-foreground">{t("p53s.howTo")}</div>}
      <div className="flex items-center gap-1">
        <input type="text" value={reason} disabled={disabled} placeholder={t("p53s.reasonPh")}
               onChange={(e) => onReason && onReason(e.target.value)}
               className="w-full rounded border px-1 py-0.5 text-[10px] dark:bg-slate-900"
               data-testid={`hfws-acct-reason-${a.coa_id}`} />
        {!has && (
          <button type="button" disabled={disabled} onClick={() => onKeepTrade && onKeepTrade()}
                  className="whitespace-nowrap rounded border px-1 py-0.5 text-[10px]"
                  data-testid={`hfws-acct-keep-${a.coa_id}`}>{t("p53s.keepTrade")}</button>
        )}
      </div>
      {has && !reason.trim() && (
        <div className="text-[10px] text-red-700" data-testid={`hfws-acct-reason-missing-${a.coa_id}`}>{t("p53s.reasonMissing")}</div>
      )}
    </div>
  );
}
